import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import MDBox from "components/MDBox";
import { useMaterialUIController } from "context";
import CTLTable from "application/components/CTLTable";
import { application } from "globals/endpoints";
import CTLNotification from "application/components/CTLNotification";
import RowAction from "application/components/RowAction";
import DefaultCell from "application/components/DefaultCell";
import CTLBooleanCell from "application/components/CTLBooleanCell";
import { CircularProgress, Box } from "@mui/material";
import CTLAdvancedFilter from "application/components/CTLAdvancedFilter";
import { getCallCenterConfigurationReports } from "../../callCenterConfiguration/component/allCallcenterRepots/reportMenu";
import MDTypography from "components/MDTypography";

function QueueReports({
  onLoad,
  pageId,
  searchText,
  registerCallBacks,
  pageName = "Queue Reports",
}) {
  const [controller, dispatch] = useMaterialUIController();
  const { activePage, ivrExtension, tableSortColumn, tableColumnDataType } =
    controller;
  const [openFilter, setOpenFilter] = useState(false);
  const [updateList, setUpdateList] = useState(Date.now());
  const [metadata, setMetaData] = useState();
  const [columnDataType, setColumnDataType] = useState("date");
  const [sortColumn, setSortColumn] = useState("recordCreatedOn");
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "",
    status: "",
  });
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [queueValue, setQueueValue] = useState("");
  useEffect(() => {
    setQueueValue(ivrExtension?.code?.queue || "");
  }, [ivrExtension]);

  const refs = useRef({
    queueValue,
    sortColumn,
    columnDataType,
    searchText,
  });

  useEffect(() => {
    refs.current = {
      queueValue,
      sortColumn,
      columnDataType,
      searchText,
    };
  }, [queueValue, sortColumn, columnDataType, searchText]);
  const callCenterConfigurationReports = getCallCenterConfigurationReports();
  const Attributes = {
    formSize: "lg",
    columns: 3,
    enableDispositionDetails: true,
    disabledAcdDelete: true,
    selectUrl: "/dynamicPopUpData/listAll/queue",
    enableRefresh: true,
    enableDownloadButton: true,
    enableAdvanceSearch: true,
    modes: callCenterConfigurationReports.map((item) => {
      return {
        id: item.key,
        name: item.name,
        iconName: item.icon,
        url: item.url,
        description: item.description,
      };
    }),
    settingsMode: false,
  };

  const operationURLS = useMemo(
    () => ({
      tableColumn: `/dynamicPopUpData/headers/${queueValue}`,
      tableData: `/dynamicPopUpData/listWithQueue/${queueValue}`,
    }),
    [queueValue]
  );

  useEffect(() => {
    if (onLoad) onLoad(Attributes);
    if (registerCallBacks) {
      registerCallBacks({
        openAdvancedFilter: onOpenFilter,
        DownloadData: onUpdateDataDownload,
        onRefresh: onRefresh,
      });
    }
  }, []);

  const onRefresh = () => {
    setUpdateList(Date.now());
    setNotify({
      isOpen: true,
      pagename: pageName,
      status: "Data refreshed successfully",
      type: "success",
    });
  };

  useEffect(() => {
    setUpdateList(Date.now());
  }, [queueValue]);

  useEffect(() => {
    {
      tableSortColumn &&
        tableSortColumn !== "" &&
        setSortColumn(tableSortColumn);
    }
    {
      tableColumnDataType &&
        tableColumnDataType !== "" &&
        setColumnDataType(tableColumnDataType);
    }
  }, [tableSortColumn, tableColumnDataType]);

  function onUpdateDataDownload() {
    const {
      queueValue: queue,
      sortColumn,
      columnDataType,
      searchText,
    } = refs.current;

    if (!queue) {
      setNotify({
        isOpen: true,
        pagename: pageName,
        status: "Please select a queue before downloading",
        type: "warning",
      });
      return;
    }
    const requestBodyData = {
      sortDirection: "desc",
      sortBy: sortColumn,
      search: searchText,
      sortDataType: columnDataType,
      advancedFilters: advancedFilters,
    };
    const requestConfig = {
      responseType: "arraybuffer",
    };
    application
      .post(
        `/dynamicPopUpData/downloadCSV/${queue}`,
        requestBodyData,
        requestConfig
      )
      .then((response) => {
        if (response) {
          setLoading(false);
          const blob = new Blob([response.data], { type: "text/csv" });
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = "Queue.csv";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(link.href);

          setNotify({
            isOpen: true,
            pagename: pageName,
            status: "CSV Download successful",
            type: "success",
          });
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 400) {
          setNotify({
            isOpen: true,
            pagename: pageName,
            status: "Unable To Download File",
            type: "error",
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    if (!queueValue) {
      setMetaData(null);
      return;
    }
    setLoading(true);
    application
      .post(`/dynamicPopUpData/headers/${queueValue}`)
      .then((response) => {
        const dynamicData = response?.data?.data || [];

        const fixedColumns = [
          {
            Header: "Record Created On",
            accessor: "recordCreatedOn",
            dataType: "date",
            allowFilter: true,
          },
          {
            Header: "Queue",
            accessor: "queue",
            dataType: "string",
          },
          {
            Header: "Queue Name",
            accessor: "queueName",
            dataType: "string",
          },
          {
            Header: "Agent Id",
            accessor: "recordCreatedBy",
            dataType: "string",
          },
          {
            Header: "Agent Name",
            accessor: "agentName",
            dataType: "string",
          },
          {
            Header: "Customer Name",
            accessor: "description",
            dataType: "string",
          },
          {
            Header: "Phone Number",
            accessor: "phoneNumber",
            dataType: "string",
          },
        ];

        const filteredDynamic = (dynamicData || [])
          .filter(
            (col) =>
              col &&
              col.accessor &&
              !fixedColumns.some((fixed) => fixed.accessor === col.accessor)
          )
          .map((col) => {
            const allowInForm = col?.allowInForm === true;
            const dataType = col?.dataType || "";

            if (
              allowInForm &&
              ["date", "dateTime", "LocalDateTime"].includes(dataType)
            ) {
              return {
                ...col,
                allowFilter: true,
                dateTime: true,
              };
            }
            return col;
          });

        setMetaData([...fixedColumns, ...filteredDynamic]);
      })
      .catch((error) => {
        console.error("Error fetching column metadata:", error);
        setMetaData([]);
      })
      .finally(() => setLoading(false));
  }, [queueValue]);

  const getRows = useCallback(
    (onDelete, onEdit) => {
      if (!Array.isArray(metadata)) return [];

      return metadata.map((item = {}) => {
        const {
          Header = "",
          accessor = "",
          controlType = "",
          dataType = "",
          allowFilter = false,
          isMandatory = false,
          dateTime = true,
          options = [],
        } = item;

        const allowInForm = item.allowInForm ?? false;
        const displayInList = allowInForm ? true : item.displayInList ?? true;

        let CellComponent;

        if (Header === "Action") {
          CellComponent = ({ row, value }) => (
            <RowAction
              row={row}
              value={value}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          );
        } else {
          switch (dataType) {
            case "Boolean":
              CellComponent = ({ row, value }) => (
                <CTLBooleanCell row={row} value={value} />
              );
              break;
            default:
              CellComponent = ({ value }) => <DefaultCell value={value} />;
          }
        }
        return {
          Header,
          accessor,
          displayInList,
          allowInForm,
          allowFilter,
          dataType,
          controlType,
          isMandatory,
          options,
          dateTime,
          Cell: CellComponent,
        };
      });
    },
    [metadata]
  );

  function onApplyFilter(advancedFilters) {
    setAdvancedFilters(advancedFilters);
    setOpenFilter(false);
  }

  function onOpenFilter() {
    setOpenFilter(true);
  }

  function onCloseFilter() {
    setOpenFilter(false);
  }
  return (
    <MDBox>
      {!queueValue ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            pt: 10,
            pb: 10,
            textAlign: "center",
            borderRadius: 3,
            boxShadow: "0 8px 20px rgba(0,0,0,0.05)",
          }}
        >
          <MDTypography
            variant="h6"
            fontWeight="bold"
            sx={{
              mb: 1,
            }}
          >
            No Queue Selected
          </MDTypography>

          <MDTypography variant="h6">
            Please select a queue to view the Disposition Analysis Queue Report.
          </MDTypography>
        </Box>
      ) : (
        metadata && (
          <>
            <CTLTable
              apiURL={`${operationURLS.tableData}`}
              advancedFilters={advancedFilters}
              columns={getRows()}
              entriesPerPage={false}
              canSearch
              defaultSortDirection="desc"
              defaultSortColumn=""
              pageId={pageId}
              list
              searchText={searchText}
              hasRowSelection={false}
              updateList={updateList}
              disablePagination={false}
            />
            <CTLAdvancedFilter
              entityName="Queue Reports"
              columns={getRows()}
              applyFilter={openFilter}
              onApplyFilter={onApplyFilter}
              onCloseFilter={onCloseFilter}
              advancedFilters={advancedFilters}
            />
          </>
        )
      )}
      <MDBox>
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100px",
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </MDBox>
      <CTLNotification notify={notify} setNotify={setNotify} />
    </MDBox>
  );
}
export default QueueReports;
